<template>
  <div id="app" :class="showVideoLoader ? 'disabled' : ''" @click="launchSound">
    <transition name="slide-fade">
      <FlashMessage
        v-if="show_flash_message"
        :message="flash_message"
        @closeFlash="hideFlashMessage"
      />
    </transition>
    <Expiration @click="checkAdult()" v-show="displayExpiration()" />

    <parentalGateway
      v-if="initialized && popAgeSetting"
      :info="info"
      :title="title"
      :sub-title="subtitle"
      :sound-enabled="soundActive"
      @update:model-value="emitParentalGateValue"
      @close="closeParentalGate"
    />
    <div
      class="back"
      :class="{
        'blur-background': applyBlurAgeSetting(),
        'search-bg': isSearchOrSettingshView,
      }"
      style="background-position: bottom"
    >
      <PromotionalBanner
        v-if="showOrangeFRBanner"
        class="promotional-banner-visibility"
        :coverImagePath="kidjoIconImage"
        :platform="platform"
        :soundEnabled="soundActive"
        :translatedLabels="translatedLabels"
        @closeMobileAppCard="onMobileAppCardClose"
        @handleStoreRouting="onMobileCardClick"
        :lang="getLang"
      />
      <div class="main-container">
        <router-view v-if="initialized" />
      </div>
      <SplashScreen v-if="!initialized" />

      <img src="@/assets/icons/cloud.svg?url" class="cloud left" alt="cloud" />
      <img src="@/assets/icons/cloud.svg?url" class="cloud right" alt="cloud" />
    </div>
    <div class="video-player" v-show="videoPlayerDisplayed">
      <div class="close" id="buttonClose" @click="goBack()">
        <font-awesome-icon icon="times" size="2x" :style="{ margin: '9px' }" />
      </div>
      <video ref="videoPlayer" controls controlslist="nodownload" playsinline />
    </div>
    <div
      v-show="showLoader || showVideoLoader"
      class="lds-facebook"
      :class="showVideoLoader ? '' : ''"
    >
      <div></div>
      <div></div>
      <div></div>
    </div>
    <NotificationPopup v-if="isNotificationVisible.status" />
  </div>
</template>

<script>
import SplashScreen from "@/components/SplashScreen.vue";
import Expiration from "@/components/Expiration.vue";
import LoopSong from "@/utils/sounds";
import userService from "@/services/user.service";
import userUtil from "@/utils/user.util";
import FlashMessage from "@/components/FlashMessage.vue";
import { soundList, soundPlayer } from "@/utils/sounds";
import partnerOrangeMixin from "@/mixins/orange";
import NotificationPopup from "@/components/NotificationPopup.vue";
import { Events } from "@/models/analytics";
import "@kidjo/kidjo-web-commons-lib/dist/style.css";
import langUtil, { StringKey } from "@/utils/lang.util";
import {
  PromotionalBanner,
  parentalGateway,
} from "@kidjo/kidjo-web-commons-lib";
import kidjoIcon from "@/assets/images/start_kidjo.png";

export default {
  components: {
    SplashScreen,
    parentalGateway,
    Expiration,
    FlashMessage,
    NotificationPopup,
    PromotionalBanner,
  },
  mixins: [partnerOrangeMixin],
  data() {
    return {
      hidden: typeof document.hidden !== "undefined" ? "hidden" : null,
      visibilityChange:
        typeof document.hidden !== "undefined" ? "visibilitychange" : null,
      showAgeSetting: false,
      initialized: false,
      videoPlayer: null,
      videoPlayerDisplayed: false,
      videoPlaying: false,
      videoRemainingTimeLimit: 0,
      videoTickIntervalId: null,
      flash_message: "",
      show_flash_message: false,
      orangeFRSourceDetected: false,
      orangeFRIntroActive: false,
      isAndroid: !!navigator.userAgent && /Android/i.test(navigator.userAgent),
      isIos:
        !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform),
      translatedLabels: {
        appName: langUtil.getLang().t(StringKey.appName),
        appDetails: langUtil.getLang().t(StringKey.appDetails),
        buttonLabel: langUtil.getLang().t(StringKey.buttonLabel),
      },
    };
  },
  computed: {
    title() {
      return langUtil.getLang().t("age_gate_welcome");
    },
    subtitle() {
      return langUtil.getLang().t("age_gate_welcome_desc");
    },
    info() {
      return langUtil.getLang().t("info_not_saved");
    },
    soundActive() {
      return this.$store.getters["settings/soundActive"];
    },
    platform() {
      return this.isIos ? "Apple" : "Google";
    },
    isSearchOrSettingshView() {
      return (
        this.$router.currentRoute.value.name === "search" ||
        this.$router.currentRoute.value.name === "settings"
      );
    },
    getLang() {
      return langUtil.getLang().currentLocale;
    },
    hideMobileCard() {
      return this.$store.getters["settings/hideMobileCard"];
    },
    kidjoIconImage() {
      return kidjoIcon;
    },
    isAgeSettingDisplayed() {
      return this.$store.getters["settings/isAgeSettingDisplayed"];
    },
    showExpiration() {
      return this.$store.getters["settings/showExpiration"];
    },
    getTimeLimit() {
      return this.$store.getters["settings/getTime"];
    },
    displayVideoLoader() {
      return this.$store.getters["settings/displayVideoLoader"];
    },
    displayLoader() {
      return this.$store.getters["settings/displayLoader"];
    },
    getToken() {
      return this.$store.getters["account/getToken"];
    },
    getPremium() {
      return this.$store.getters["account/getPremium"];
    },
    isHomeView() {
      return this.$route.fullPath === "/";
    },
    showVideoLoader: {
      get() {
        return this.displayVideoLoader;
      },
    },
    showLoader: {
      get() {
        return this.displayLoader;
      },
    },
    popAgeSetting: {
      get() {
        if (this.isAgeSettingDisplayed) {
          this.showAgeSetting = true;
        } else {
          this.showAgeSetting = false;
        }

        return this.showAgeSetting;
      },
    },
    isNotificationVisible() {
      return this.$store.getters["settings/getNotificationVisibility"];
    },
    isOrangeFRSource() {
      return (
        this.orangeFRSourceDetected ||
        localStorage.getItem(process.env.VUE_APP_SOURCE_STORAGE_KEY) ===
          process.env.VUE_APP_ORANGE_FR_SOURCE
      );
    },
    showOrangeFRBanner() {
      return this.isHomeView && !this.hideMobileCard;
    },
  },
  methods: {
  setVideoIsPaused() {
    this.$store.dispatch('settings/setVideoIsPaused');
  },
  closeParentalGate() {
    this.$store.dispatch('settings/displayAgeSetting');
  },
  emitParentalGateValue(event) {
    const now = new Date();
    const allowedBirthDate = now.getFullYear() - 18;

    if (event <= allowedBirthDate && event >= 1900) {
       if (this.$route.name === 'player') {
          this.setVideoIsPaused(false);
          this.$router.push({ name: 'settings' });
        } else {
          this.$router.push({ name: 'settings' });
          this.closeParentalGate();
        }
    } else {
      this.$store.dispatch("settings/setNotificationVisibility", {
        status: true,
        title: 'text_oops',
        message: 'video_selection_toast_too_young',
        closeButton: true
      });
      this.closeParentalGate();
    }
  },
    setHideMobileCard(set) {
      this.$store.dispatch("settings/setHideMobileCard", set);
    },
    isIOSDevice() {
      return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      );
    },
    isAndroidDevice() {
      return /Android/.test(navigator.userAgent);
    },

    onMobileCardClick() {
      // Orange FR behavior
      if (this.isOrangeFRSource) {
        if (this.isAndroidDevice()) {
          window.location.href = process.env.VUE_APP_ORANGE_FR_ANDROID_URL;
          return;
        }
        if (this.isIOSDevice()) {
          window.location.href = process.env.VUE_APP_ORANGE_FR_IOS_URL;
          return;
        }
        // Fallback (desktop): choose iOS or Android; usually Android or a landing page.
        window.location.href = process.env.VUE_APP_ORANGE_FR_ANDROID_URL;
        return;
      }
      window.location.href = "https://www.kidjo.tv/apps/tv";
    },
    onMobileAppCardClose() {
      this.isMobileAppCardVisible = false;
      this.setHideMobileCard(true);
    },
    setShowExpiration(val) {
      this.$store.dispatch("settings/setShowExpiration", val);
    },
    displayAgeSetting(val) {
      this.$store.dispatch("settings/displayAgeSetting", val);
    },
    setTimeLimit(val) {
      this.$store.dispatch("settings/setTimeLimit", val);
    },
    getUserInfo(val) {
      return this.$store.dispatch("account/getUserInfo", val);
    },
    checkUserKey(val) {
      return this.$store.dispatch("account/checkUserKey", val);
    },
    showFlashMessage(content) {
      this.flash_message = content;
      this.show_flash_message = true;
    },
    hideFlashMessage() {
      this.show_flash_message = false;
    },
    handleKeyboard(event) {
      event.preventDefault();
      try {
        switch (event.keyCode) {
          case VK_BACK_SPACE: {
            this.handleBack();
            // Do not handle back on Vewd TV as the remote manipulates the history already for this key code
            break;
          }
          case VK_BACK: {
            this.handleBack();
            // Do not handle back on Vewd TV as the remote manipulates the history already for this key code
            break;
          }
          case 461: {
            this.handleBack();
            break;
          }
          default: {
            if (this.videoRemainingTimeLimit <= 0 && this.showExpiration) {
              this.checkAdult();
            }
          }
        }
      } catch (error) {
        switch (event.code) {
          case "Escape": {
            this.handleBack();
            break;
          }
        }
      }

      this.videoRemainingTimeLimit = this.getTimeLimit;
    },
    // goBack() {
    //   soundPlayer(soundList.general);
    //   // Stop video playback before navigating away
    //   if (this.videoPlayer && this.videoPlayer.src) {
    //     this.resetAndCleanVideo();
    //   }
    //   this.handleBack();
    // },
    goBack() {
      soundPlayer(soundList.general);

      // Special case: Orange FR intro close should ALWAYS go to home
      if (this.orangeFRIntroActive) {
        if (this.videoPlayer && this.videoPlayer.src) {
          this.resetAndCleanVideo();
        }
        this.videoPlayerDisplayed = false;
        this.orangeFRIntroActive = false;

        this.$router.replace({ name: "home" });
        return;
      }

      // existing logic
      if (this.videoPlayer && this.videoPlayer.src) {
        this.resetAndCleanVideo();
      }
      this.handleBack();
    },
    handleBack() {
      const currentRoute = this.$router.currentRoute;
      const lastRouteName = localStorage.getItem("lastRouteName");

      if (this.isAgeSettingDisplayed) {
        this.displayAgeSetting(false);
      } else if (currentRoute.name !== "home" && lastRouteName) {
        lastRouteName === "search"
          ? this.$router.replace({ name: lastRouteName })
          : this.$router.back();
      } else if (currentRoute.name !== "home") {
        this.$router.replace({ name: "home" });
      } else if (currentRoute.name === "home") {
        const win = window.open("", "_self");
        win.close();
      }
    },
    displayExpiration() {
      return this.showExpiration;
    },
    checkAdult() {
      if (this.videoRemainingTimeLimit !== -60) {
        if (this.videoRemainingTimeLimit <= 0) {
          this.setShowExpiration(false);
          this.displayAgeSetting(true);
        }
      }
    },
    applyBlurAgeSetting() {
      return this.showAgeSetting || this.showExpiration;
    },
    launchSound() {
      if (
        this.videoPlayerDisplayed ||
        this.$route.name === "player" ||
        this.orangeFRIntroActive
      ) {
        this.pauseBackgroundMusic();
        return;
      }
      LoopSong.play();
    },
    onVideoPlay() {
      if (this.videoPlayer.src && !this.videoPlaying) {
        this.videoRemainingTimeLimit = this.getTimeLimit;

        if (this.videoRemainingTimeLimit !== -60) {
          // Start video ticking if limit is set (not infinite)
          this.videoTickIntervalId = setInterval(() => {
            this.onVideoTick();
          }, 1000);
        }

        this.videoPlaying = true;
      }
    },
    onVideoPause() {
      if (this.videoPlaying) {
        this.clearVideoTick();
        this.videoPlaying = false;
      }
    },
    onVideoTick() {
      this.videoRemainingTimeLimit -= 1;
      this.setTimeLimit(this.videoRemainingTimeLimit);
      // Check if limit is reached
      if (this.videoRemainingTimeLimit <= 0) {
        this.clearVideoTick();
        this.videoRemainingTimeLimit = 0;
        this.videoPlayer.pause();
        this.setShowExpiration(true);
      }
    },
    clearVideoTick() {
      if (this.videoTickIntervalId) {
        clearInterval(this.videoTickIntervalId);
        this.videoTickIntervalId = null;
        this.setTimeLimit(this.videoRemainingTimeLimit);
      }
    },
    resetAndCleanVideo() {
      this.clearVideoTick();
      this.videoPlayer.pause();
      this.videoPlaying = false;
      this.videoPlayer.removeAttribute("src");
      this.videoPlayer.srcObject = null;
    },
    playOrangeFRIntroIfNeeded() {
      if (!this.orangeFRSourceDetected) return;

      // Show intro only once per tab/session (you can use localStorage if you want "once ever")
      if (
        sessionStorage.getItem(
          process.env.VUE_APP_ORANGE_FR_INTRO_SHOWN_KEY,
        ) === "1"
      )
        return;
      sessionStorage.setItem(
        process.env.VUE_APP_ORANGE_FR_INTRO_SHOWN_KEY,
        "1",
      );

      // Show existing fullscreen player
      this.orangeFRIntroActive = true;
      this.videoPlayerDisplayed = true;

      // Wait for DOM to update and video element to be available
      this.$nextTick(() => {
        const videoElement = this.$refs.videoPlayer;
        if (videoElement) {
          // Pause background music first
          LoopSong.pause();

          videoElement.setAttribute("playsinline", "");
          videoElement.setAttribute("webkit-playsinline", ""); // Safari legacy
          videoElement.muted = true; // Start muted for autoplay
          videoElement.autoplay = true;
          videoElement.preload = "auto";
          videoElement.src = process.env.VUE_APP_ORANGE_FR_INTRO_VIDEO;
          videoElement.load();

          // Wait for loadedmetadata event before playing
          videoElement.addEventListener(
            "loadedmetadata",
            () => {
              const playPromise = videoElement.play();
              if (playPromise && typeof playPromise.catch === "function") {
                playPromise
                  .then(() => {
                    // DO NOT unmute here — keep muted
                  })
                  .catch((error) => {
                    console.warn("Autoplay blocked:", error);
                  });
              }
            },
            { once: true },
          );
        }
      });
    },
    pauseBackgroundMusic() {
      try {
        LoopSong.pause();
      } catch (e) {}
    },
    resumeBackgroundMusicIfAllowed() {
      try {
        // only play if NOT in player and NOT in orange intro overlay
        if (
          this.$route.name !== "player" &&
          !this.videoPlayerDisplayed &&
          !this.orangeFRIntroActive
        ) {
          LoopSong.play();
        }
      } catch (e) {}
    },
    handleVisibilityChange() {
      const doc = document;
      if (doc[this.hidden]) {
        LoopSong.pause();
      } else {
        this.resumeBackgroundMusicIfAllowed();
      }
    },
  },
  async mounted() {
    document.addEventListener(
      this.visibilityChange,
      this.handleVisibilityChange,
      false,
    );
    // check Auth
    const params = new URLSearchParams(window.location.search);
    const sourceParam = params.get("source");
    if (sourceParam === process.env.VUE_APP_ORANGE_FR_SOURCE) {
      localStorage.setItem(
        process.env.VUE_APP_SOURCE_STORAGE_KEY,
        process.env.VUE_APP_ORANGE_FR_SOURCE,
      );
      this.orangeFRSourceDetected = true;

      // Optional but recommended: clean URL so it doesn't re-trigger on internal nav
      if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else {
      // If stored already from earlier landing, keep behavior for this session
      this.orangeFRSourceDetected =
        localStorage.getItem(process.env.VUE_APP_SOURCE_STORAGE_KEY) ===
        process.env.VUE_APP_ORANGE_FR_SOURCE;
      this.launchSound();
    }

    if (params.has("key")) {
      await this.checkUserKey(params.get("key")).then((response) => {
        this.$store
          .dispatch("account/getUserInfo", response.token)
          .then(() => {
            this.$store.dispatch("analytics/postAnalytics", Events.OPEN_APP, {
              root: true,
            });
          })
          .catch(() => {
            this.initialized = true;
            localStorage.removeItem("kidj_ut");
          });
      });
    }
    if (
      this.getToken !== false &&
      localStorage.getItem("kidj_ut") &&
      !params.has("key")
    ) {
      await this.$store
        .dispatch("account/getUserInfo", localStorage.getItem("kidj_ut"))
        .then(() => {
          this.$store
            .dispatch("analytics/postAnalytics", Events.OPEN_APP, {
              root: true,
            })
            .then(() => {
              this.initialized = true;
            });
        })
        .catch(() => {
          this.initialized = true;
          localStorage.removeItem("kidj_ut");
        });
    } else {
      this.$store.dispatch("analytics/postAnalytics", Events.OPEN_APP, {
        root: true,
      });
    }
    await userService.refreshWeb().then((response) => {
      userUtil.setDeviceData(response);
    });

    this.initialized = true;

    // Bind video player to document
    const anyDocument = document;
    anyDocument.videoPlayer = this.$refs.videoPlayer;
    this.videoPlayer = anyDocument.videoPlayer;
    this.videoPlayer.resetAndClean = this.resetAndCleanVideo;
    this.videoPlayer.addEventListener("play", this.onVideoPlay);
    this.videoPlayer.addEventListener("pause", this.onVideoPause);
    this.videoPlayerDisplayed = this.$route.name === "player";
    this.playOrangeFRIntroIfNeeded();
    document.addEventListener("keyup", this.handleKeyboard);
  },
  unmounted() {
    // Remove listeners
    if (this.videoPlayer) {
      this.videoPlayer.removeEventListener("play", this.onVideoPlay);
      this.videoPlayer.removeEventListener("pause", this.onVideoPause);
    }

    document.removeEventListener("keyup", this.handleKeyboard);
    sessionStorage.removeItem(process.env.VUE_APP_ORANGE_FR_INTRO_SHOWN_KEY);
    sessionStorage.removeItem(process.env.VUE_APP_SOURCE_STORAGE_KEY);
    this.clearVideoTick();
    document.removeEventListener(
      this.visibilityChange,
      this.handleVisibilityChange,
      false,
    );
  },
  watch: {
    $route() {
      this.videoPlayerDisplayed = this.$route.name === "player";

      // pause when entering player route, resume when leaving (only if no overlay)
      if (this.$route.name === "player") {
        this.pauseBackgroundMusic();
      } else {
        this.resumeBackgroundMusicIfAllowed();
      }
    },

    videoPlayerDisplayed(val) {
      // Pause any time overlay becomes visible
      if (val) this.pauseBackgroundMusic();
      else this.resumeBackgroundMusicIfAllowed();
    },

    orangeFRIntroActive(val) {
      // Pause during Orange intro, resume after it ends (if not in player)
      if (val) this.pauseBackgroundMusic();
      else this.resumeBackgroundMusicIfAllowed();
    },
  },
};
</script>

<!-- <style lang="scss">
  @font-face {
    font-family: "Nunito-ExtraBold";
    src: url('@/assets/fonts/Nunito-ExtraBold.ttf') format('truetype');
  }

  @font-face {
    font-family: "Nunito-Regular";
    src: url('@/assets/fonts/Nunito-Regular.ttf') format('truetype');
  }

  .lds-facebook {
    background-color: #fff;
    border-radius: 8px;
    bottom: 0;
    box-shadow: 1px 1px 10px 1px rgba(76, 76, 76, 0.44);
    display: inline-block;
    height: 80px;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
    width: 80px;
    z-index: 99999;
  }

  .lds-facebook div {
    display: inline-block;
    position: absolute;
    left: 6px;
    width: 16px;
    background: #9AD5F0;
    animation: lds-facebook 1.2s cubic-bezier(0, 0.5, 0.5, 1) infinite;
  }

  .lds-facebook div:nth-child(1) {
    left: 8px;
    animation-delay: -0.24s;
  }

  .lds-facebook div:nth-child(2) {
    left: 32px;
    animation-delay: -0.12s;
  }

  .lds-facebook div:nth-child(3) {
    left: 56px;
    animation-delay: 0s;
  }

  @keyframes lds-facebook {
    0% {
      top: 8px;
      height: 64px;
    }

    50%,
    100% {
      top: 25px;
      height: 32px;
    }
  }

  // Handle iOS notch
  html { padding: env(safe-area-inset); }

  html,
  body {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;

    /* Prevents the 'bounce' and the refresh trigger */
    overscroll-behavior: none;
    overscroll-behavior-y: none;
    
    /* Ensure the app stays contained */
    height: 100%;
    overflow: hidden;
  }

  #app {
    height: 100%;
    width: 100%;
    position: relative;
    // -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;

    @media (orientation: landscape) {
      position: fixed;
    }
  }
// .main-container {
//   height: 100%;
//   overflow: visible;        /* or remove overflow entirely */
//   min-height: 0;            /* helps flex children */
//   touch-action: pan-x pan-y;/* helps Android route gestures */
// }
  .main-container {
    z-index: 1;
    top: 0;
     height: 100%;
    overflow: visible;        /* or remove overflow entirely */
    min-height: 0;            /* helps flex children */
    touch-action: pan-x pan-y;

    @media (orientation: landscape) or (min-width: 1024px) {
      position: absolute;
    }
    @supports (-webkit-overflow-scrolling: touch) {
      -webkit-overflow-scrolling: touch;
    }
  }

  .back {
    inset: 0;
    /* keep background */
    overflow: visible; /* IMPORTANT: don't block children gestures */
    min-height: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    // overflow: hidden;
    background: white url('@/assets/backgrounds/main_bg.svg?url') no-repeat;
    background-size: cover;
  }

  .search-bg {
    width: 100%;
    height: 100%;
    background-image: url("@/assets/backgrounds/bg_stars.svg?url");
    background-color: #29b6f6;
    background-position: top;
    background-size: cover;
    background-repeat: no-repeat;
  }
  .blur-background {
    height: 100%;
    width: 100%;
    filter: blur(12px);
  }

  .view {
    height: 100%;
    width: 100%;
  }

  .cloud {
    display: none;
    position: absolute;
    width: 115px;

    &.left {
      top: 55%;
      left: -47px;
    }

    &.right {
      top: 35%;
      right: -36px;
    }

    @media (max-width: 550px) {
      display: block;
    }
  }

  .video-player {
    height: 100%;
    position: relative;
    width: 100%;
    z-index: 100;
    overflow: hidden;
    left: 0px;
    right: 0px;

    video {
      height: 100%;
      width: 100%;
    }
  }

  .cc-revoke.cc-bottom {
    @media screen and (max-height: 850px) {
      display: none;
    }
    @media screen and (max-height: 600px) {
      display: none;
    }
    @media only screen and (max-width: 420px) {
      display: none;
    }
  }
</style> -->

<style lang="scss">
@font-face {
  font-family: "Nunito-ExtraBold";
  src: url("@/assets/fonts/Nunito-ExtraBold.ttf") format("truetype");
}

@font-face {
  font-family: "Nunito-Regular";
  src: url("@/assets/fonts/Nunito-Regular.ttf") format("truetype");
}

.lds-facebook {
  background-color: #fff;
  border-radius: 8px;
  bottom: 0;
  box-shadow: 1px 1px 10px 1px rgba(76, 76, 76, 0.44);
  display: inline-block;
  height: 80px;
  left: 0;
  margin: auto;
  position: absolute;
  right: 0;
  top: 0;
  width: 80px;
  z-index: 99999;
}

.lds-facebook div {
  display: inline-block;
  position: absolute;
  left: 6px;
  width: 16px;
  background: #9ad5f0;
  animation: lds-facebook 1.2s cubic-bezier(0, 0.5, 0.5, 1) infinite;
}

.lds-facebook div:nth-child(1) {
  left: 8px;
  animation-delay: -0.24s;
}

.lds-facebook div:nth-child(2) {
  left: 32px;
  animation-delay: -0.12s;
}

.lds-facebook div:nth-child(3) {
  left: 56px;
  animation-delay: 0s;
}

@keyframes lds-facebook {
  0% {
    top: 8px;
    height: 64px;
  }

  50%,
  100% {
    top: 25px;
    height: 32px;
  }
}

/* Handle iOS notch */
html {
  padding: env(safe-area-inset);
}

html,
body {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;

  overscroll-behavior: none;
  overscroll-behavior-y: none;
  overflow: hidden;
  touch-action: pan-x pan-y;
}

#app {
  height: 100%;
  width: 100%;
  position: relative;
  overscroll-behavior: none;

  min-height: 0;
  touch-action: pan-x pan-y;

  @media (orientation: landscape) {
    position: fixed;
  }
}

.main-container {
  z-index: 1;
  top: 0;
  width: 100%;
  height: 100%;
  position: relative;

  overflow: visible;

  min-height: 0;
  touch-action: pan-x pan-y;

  @media (orientation: landscape) or (min-width: 1024px) {
    // position: absolute;
  }
  @supports (-webkit-overflow-scrolling: touch) {
    -webkit-overflow-scrolling: touch;
  }
}

.back {
  position: absolute;
  width: 100%;
  height: 100%;

  overflow: visible;

  background: white url("@/assets/backgrounds/main_bg.svg?url") no-repeat;
  background-size: cover;

  min-height: 0;
  touch-action: pan-x pan-y;
}

.search-bg {
  width: 100%;
  height: 100%;
  background-image: url("@/assets/backgrounds/bg_stars.svg?url");
  background-color: #29b6f6;
  background-position: top;
  background-size: cover;
  background-repeat: no-repeat;
}

.blur-background {
  height: 100%;
  width: 100%;
  filter: blur(12px);
}

.view {
  height: 100%;
  width: 100%;
}

.cloud {
  display: none;
  position: absolute;
  width: 115px;

  &.left {
    top: 55%;
    left: -47px;
  }

  &.right {
    top: 35%;
    right: -36px;
  }

  @media (max-width: 550px) {
    display: block;
  }
}

.video-player {
  height: 100%;
  position: relative;
  width: 100%;
  z-index: 100;
  overflow: hidden;
  left: 0px;
  right: 0px;

  video {
    height: 100%;
    width: 100%;
  }
}

.cc-revoke.cc-bottom {
  @media screen and (max-height: 850px) {
    display: none;
  }
  @media screen and (max-height: 600px) {
    display: none;
  }
  @media only screen and (max-width: 420px) {
    display: none;
  }
}
</style>
<style scoped lang="scss">
@mixin transition($transition) {
  -moz-transition: $transition;
  -o-transition: $transition;
  -webkit-transition: $transition;
  transition: $transition;
}
.video-player {
  background-color: black;
}
.close {
  background: white;
  border-radius: 25px;
  color: #fd9188;
  cursor: pointer;
  height: 50px;
  position: absolute;
  right: 25px;
  text-align: center;
  top: 25px;
  width: 50px;
  z-index: 110;

  &:active,
  &:focus,
  &:hover {
    background: #ddd;
    @include transition(background 0.2s ease);
  }
  @media (max-width: 768px) {
    right: 5px;
    top: 60px;
    width: 25px;
    height: 25px;
    padding: 6px;
    svg {
      margin: 0px !important;
      font-size: 1.5em !important;
    }
  }
  @media (max-width: 1023px) {
    right: 5px;
    top: 60px;
    width: 25px;
    height: 25px;
    padding: 6px;
    svg {
      margin: 0px !important;
      font-size: 1.5em !important;
    }
  }
}
.disabled {
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: black; // Adjust for your needs
    z-index: 999; // Ensure it’s on top of everything
    pointer-events: auto; // Allow clicks on the overlay
  }
  pointer-events: none;
}
.promotional-banner-visibility {
  @media (orientation: landscape) {
    display: none;
  }
  @media (min-width: 1024px) {
    display: none;
  }
}
</style>
<style lang="css" scoped>
:deep(.folder-card-list > .swiper .swiper-slide) {
  padding-inline: 5px;
}
@media (orientation: landscape) and (max-height: 300px) and (min-width: 460px) {
  :deep(.top-bar .user-controls .settings) {
    height: 43px !important;
    padding: 0 8px !important;
    font-size: 16px !important;
    gap: 10px;
  }
  :deep(.search) {
    width: 41px !important;
    height: 41px !important;
  }
  :deep(.live-btn) {
    width: 78px !important;
    height: 78px !important;
  }
  :deep(.kidjo-home) {
    width: 41px !important;
    height: 41px !important;
  }
  :deep(.home-btn img) {
    width: 41px !important;
    height: 41px !important;
  }
  :deep(.top-bar) {
    height: 48px !important;
  }
  :deep(.top-bar .user-controls .settings img) {
    width: 28px !important;
    height: 28px !important;
  }
}

@media (orientation: landscape) and (max-height: 300px) and (min-width: 768px) {
  :deep(.top-bar .user-controls .settings) {
    height: 43px !important;
    padding: 0 8px !important;
    font-size: 16px !important;
  }
  :deep(.search) {
    width: 41px !important;
    height: 41px !important;
  }
  :deep(.live-btn) {
    width: 78px !important;
    height: 78px !important;
  }
  :deep(.kidjo-home) {
    width: 41px !important;
    height: 41px !important;
  }
  :deep(.home-btn img) {
    width: 41px !important;
    height: 41px !important;
  }
  :deep(.top-bar) {
    height: 48px !important;
  }
  :deep(.top-bar .user-controls .settings img) {
    width: 28px !important;
    height: 28px !important;
  }
}

@media (orientation: landscape) and (max-height: 220px) and (min-width: 500px) {
  :deep(.top-bar .user-controls .settings) {
    height: 36px !important;
    padding: 0 6px !important;
    font-size: 14px !important;
  }
  :deep(.top-bar .user-controls .settings span) {
    margin-inline: 5px;
  }
  :deep(.search) {
    width: 36px !important;
    height: 36px !important;
  }
  :deep(.live-btn) {
    width: 73px !important;
    height: 73px !important;
  }
  :deep(.kidjo-home) {
    width: 36px !important;
    height: 36px !important;
  }
  :deep(.home-btn img) {
    width: 36px !important;
    height: 36px !important;
  }
  :deep(.top-bar) {
    height: 38px !important;
  }
  :deep(.top-bar .user-controls .settings img) {
    width: 24px !important;
    height: 24px !important;
  }
}
:deep(.banner-cover) {
  z-index: 3000 !important;
}

 @media (orientation: landscape) and (max-height: 395px) {
    :deep(.card-download-button) {
        font-size: 16px;
        padding: 5px 10px;
    }
    :deep(.card-download-button .download-icon) {
      padding-top: 2px;
    }
}
</style>
